import isNil from 'lodash/isNil';
import {
  FunnelOrderKey,
  FunnelQuerySettingKey,
  IEventConditionFieldsFragment,
  IExecuteBasicInput,
  IFunnelExecuteInput,
  IFunnelStepInput,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import {
  IBuilder,
  IBuilderInputPayload,
  RowType,
} from '../../../../redux/slices/oldBuilder';
import { parseEventConditionToInput, toDatePickerInput } from '../builderUtils';
import { parseCohortRowsToCohortRefInput } from '../../../../utils/builder/cohorts';
import {
  llmChatMessageToInput,
  prepareBreakdownInput,
} from '../validateExecutionInput';
import { partitionValuesToInput } from '../partitionUtils';
import { SUBJECT_EVENT_KEY } from '../../../Chart/constants/utils';
import {
  isEmptyEventCondition,
  isEmptyEventConditions,
} from '../../../../utils/builder';
import { assertCondition } from '../../../../utils/types';
import { parseFilterToInput } from '../../../../utils/filterFields';

/**
 * if conversion window is selected, the step.partitionValue cannot be null.
 * return stepConversionInput by traverse builder.steps
 */
const parseStepConversionsToInput = (
  steps: IBuilder['steps'],
): IFunnelExecuteInput['stepConversions'] => {
  // parse stepConversion to Input, skip on index = 0 since step0 doesn't support partition
  return steps.reduce<IFunnelExecuteInput['stepConversions']>(
    (acc, step, index) => {
      if (
        index !== 0 &&
        step.partitionValue.partition.__typename === 'ConversionWindowPartition'
      ) {
        const stepConversionWindow = step.partitionValue.conversionWindow;
        acc.push({
          seq: index,
          conversionWindow: !stepConversionWindow
            ? null
            : {
                timeUnit: stepConversionWindow.timeUnit,
                value: stepConversionWindow.value,
              },
        });
      }
      return acc;
    },
    [],
  );
};

const parseStep = (
  negativeSteps: IEventConditionFieldsFragment[],
  schemaId: string,
  rowIndex: number | null,
  name: string | null = null,
): IFunnelStepInput => {
  const stepConditions = negativeSteps.map((eventCondition) =>
    parseEventConditionToInput(eventCondition, schemaId),
  );

  return { seq: rowIndex, stepConditions, name };
};

/**
 * This function is used to generate valid funnel step related input.
 * Funnel Execute will dropoff any invalid input which has empty events.
 * if any step is droppped off, also dropoff it's stepConversion, order and negativeStep
 *
 * @param state old redux builder state
 * @returns input required and valid states
 */
const parseFunnelStepsInput = (
  state: IBuilder,
): Pick<
  IFunnelExecuteInput,
  'steps' | 'negativeSteps' | 'stepConversions' | 'stepOrders'
> => {
  const { selectedOptions } = state;
  assertCondition(selectedOptions?.type === QueryType.Funnel);
  // only use step conversion in (order !== ANY)
  const parsedStepConversions =
    selectedOptions.funnelOrder !== FunnelOrderKey.Any
      ? parseStepConversionsToInput(state.steps)
      : [];
  const stepConversions: IFunnelExecuteInput['stepConversions'] = [];
  const steps: IFunnelExecuteInput['steps'] = [];
  const negativeSteps: IFunnelExecuteInput['negativeSteps'] = [];
  const stepOrders: IFunnelExecuteInput['stepOrders'] = [];

  for (let i = 0, resultIndex = 0; i < state.steps.length; i++) {
    const currentEventConditions = state.steps[i].eventConditions;
    // if current eventConditions doesn't have any valid event input, skip to the next step
    if (
      !currentEventConditions ||
      isEmptyEventConditions(currentEventConditions)
    ) {
      // special case: if the last step is empty, it means we need to remove the last item in
      // custom step order, otherwise, we will have an extra order from below when assigning stepOrders
      if (
        selectedOptions.funnelOrder === FunnelOrderKey.Custom &&
        i === state.steps.length - 1
      ) {
        stepOrders.pop();
      }
    } else {
      // assign step conversion
      const foundConversion = parsedStepConversions.find(
        (stepConversion) => stepConversion.seq === i,
      );
      // stepConversion required property: seq.  if we use step index here, it will larger than the
      // length of valid steps, therefore we need to use ConversionIndex/validStepIndex
      // to assign the new seq number to the found stepConversion
      if (foundConversion) {
        stepConversions.push({ ...foundConversion, seq: resultIndex });
      }
      // assign step order, last step doesn't have the order
      // e.g. if the step 2 is empty, then skip the second order.
      if (
        selectedOptions.funnelOrder === FunnelOrderKey.Custom &&
        i !== state.steps.length - 1
      ) {
        const currStepOrder =
          state.stepOrders[i] || selectedOptions.funnelCustomOrder;
        stepOrders.push(currStepOrder);
      }

      // assign negative steps
      const selectedNegativeSteps = state.steps[i].negativeSteps;
      if (selectedNegativeSteps) {
        const nonEmptyNegativeSteps = selectedNegativeSteps.filter(
          (negativeStep: IEventConditionFieldsFragment) =>
            !isEmptyEventCondition(negativeStep),
        );
        // if there is non empty negative step
        if (nonEmptyNegativeSteps.length > 0) {
          const negativeStepsInput = parseStep(
            nonEmptyNegativeSteps,
            state.activeSchemaId,
            i,
          );
          // same reason as stepConversion, we need to update the resultIndex into seq
          negativeSteps.push({ ...negativeStepsInput, seq: resultIndex });
        }
      }

      // assign event
      const nonEmptyEventConditions = currentEventConditions.filter(
        (eventCondition) => !isEmptyEventCondition(eventCondition),
      );
      if (nonEmptyEventConditions.length > 0) {
        const stepName = state.steps[i].name ?? null;
        const stepInput = parseStep(
          nonEmptyEventConditions,
          state.activeSchemaId,
          null,
          stepName,
        );
        steps.push(stepInput);
      }

      resultIndex++;
    }
  }

  return {
    steps,
    negativeSteps,
    stepConversions,
    stepOrders,
  };
};

const prepareFunnelExecuteInput = ({
  state,
  replaceReport,
  analysisName,
  analysisDescription,
}: IBuilderInputPayload): IFunnelExecuteInput => {
  const { selectedOptions, dateInput, llmContext } = state;

  assertCondition(
    selectedOptions?.type === QueryType.Funnel &&
      selectedOptions.mode &&
      selectedOptions.subject,
  );

  const basicInput: IExecuteBasicInput = {
    datePicker: toDatePickerInput(dateInput.datePicker),
    comparisonConfig: null, // comparisonConfig is not supported in Funnel
    dateKind: selectedOptions.dateKind,
    unit: selectedOptions.timeUnit,
    schemaId: state.activeSchemaId,
    filter: state?.filter
      ? parseFilterToInput(state.filter, state.activeSchemaId)
      : null,
    sourceId: state.sourceId ?? null,
    breakdown: prepareBreakdownInput(state),
    cohortRefs: parseCohortRowsToCohortRefInput(
      state[RowType.globalCohorts],
      state.activeSchemaId,
    ),
    timeWindow: null,
    samplePercentage: null,
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
  };

  const input: IFunnelExecuteInput = {
    basicInput,
    mode: selectedOptions.mode,
    partitions: partitionValuesToInput(selectedOptions.partitionValues),
    subject: selectedOptions.subject,
    order: selectedOptions.funnelOrder,
    groupby: null,
    negativeSteps: null,
    stepOrders: null,
    steps: null,
    stepConversions: [],
    settings: state.additionalSettings as FunnelQuerySettingKey[],
    llmChatMessages: llmContext.previousMessages.map(llmChatMessageToInput),
  };

  if (
    state.additionalSettings.includes(FunnelQuerySettingKey.Sampling) &&
    state.formulaSamplingRate !== null
  ) {
    basicInput.samplePercentage = state.formulaSamplingRate;
  }

  if (input.subject !== SUBJECT_EVENT_KEY && selectedOptions?.groupby) {
    input.groupby = selectedOptions.groupby;
  }

  if (state.steps.length) {
    const { steps, negativeSteps, stepConversions, stepOrders } =
      parseFunnelStepsInput(state);

    input.steps = steps;
    input.negativeSteps = negativeSteps;
    input.stepConversions = stepConversions;
    input.stepOrders = stepOrders;
  }

  if (!isNil(state.timeWindow)) {
    basicInput.timeWindow = state.timeWindow;
  }

  return input;
};

export default prepareFunnelExecuteInput;
